import * as React from 'react'
import {
  useGetTagMappingJobDetailQuery,
  usePreviewCanaryHistoryMappingMutation,
  usePreviewPIHistoryMappingMutation,
  useSuggestCanaryHistoryMappingMutation,
  useSuggestPIHistoryMappingMutation,
  type CanaryHistoryMappingInput,
  type PIHistoryMappingInput,
  type TagMappingLinkExclude,
  type TagMappingPreviewResult
} from '@axilon-apps/domains-ontology-ui-state'
import { Loader2, Sparkles } from 'lucide-react'

import { JobLogs } from '../../../components/JobLogs/JobLogs'
import { Button } from '../../../components/ui/button'
import { Checkbox } from '../../../components/ui/checkbox'
import { Label } from '../../../components/ui/label'
import { Textarea } from '../../../components/ui/textarea'
import {
  buildExternalHistoryMappingRequest,
  EXTERNAL_HISTORY_LABELS,
  externalHistoryCredentialsReady,
  type ExternalHistoryCredentials,
  type ExternalHistoryProvider,
  type ExternalHistorySource
} from './externalHistory'
import {
  addManualLinks,
  excludeStoredLink,
  isManualLink,
  parseStoredGrammars,
  parseStoredManualLinks,
  removeManualLink,
  serializeStoredGrammars,
  serializeStoredManualLinks
} from './tagMappingGrammars'
import { TagMappingLinkDialog, type TagLinkSource } from './TagMappingLinkDialog'
import { TagMappingPreviewTables } from './TagMappingPreview'

const PREVIEW_DEBOUNCE_MS = 350

export interface ExternalHistoryMappingStudioProps {
  provider: ExternalHistoryProvider
  siteId: string
  branchId?: string
  groupId?: string
  baseUrl: string
  credentials: ExternalHistoryCredentials
  skipTlsVerify: boolean
  nameFilter?: string
  /** Canary only: the view / dataset / folder the probe browses from. */
  rootPath?: string
  historySources: ExternalHistorySource[]
  grammarsJson: string
  manualLinksJson: string
  suggestJobId?: string
  additionalContext?: string
  onHistorySourcesChange: (sources: ExternalHistorySource[]) => void
  onGrammarsChange: (value: string) => void
  onManualLinksChange: (value: string) => void
  onSuggestJobIdChange: (value: string) => void
  onAdditionalContextChange: (value: string) => void
  onMappingValidChange: (valid: boolean) => void
}

function errorMessage(error: unknown, records: string): string {
  if (typeof error === 'object' && error !== null) {
    const record = error as Record<string, unknown>
    if (record.data && typeof record.data === 'object') {
      const data = record.data as Record<string, unknown>
      if (typeof data.error === 'string') return data.error
    }
    if (typeof record.message === 'string') return record.message
  }
  return `Could not map ${records}.`
}

function mappingIsValid(preview: TagMappingPreviewResult): boolean {
  return preview.matchCount > 0 && !preview.issues.some((issue) => issue.severity === 'error')
}

/**
 * Discover-and-approve studio for binding an external historian (PI, Canary)
 * to existing Ignition / OPC tags. The provider selects the suggest / preview
 * endpoints and the credential shape; the grammar review is shared.
 */
export function ExternalHistoryMappingStudio(props: ExternalHistoryMappingStudioProps) {
  const labels = EXTERNAL_HISTORY_LABELS[props.provider]
  const grammars = React.useMemo(
    () => parseStoredGrammars(props.grammarsJson),
    [props.grammarsJson]
  )
  const manuals = React.useMemo(
    () => parseStoredManualLinks(props.manualLinksJson),
    [props.manualLinksJson]
  )
  const [preview, setPreview] = React.useState<TagMappingPreviewResult>()
  const [linkSource, setLinkSource] = React.useState<TagLinkSource | null>(null)
  const [suggestPI, suggestPIState] = useSuggestPIHistoryMappingMutation()
  const [previewPI, previewPIState] = usePreviewPIHistoryMappingMutation()
  const [suggestCanary, suggestCanaryState] = useSuggestCanaryHistoryMappingMutation()
  const [previewCanary, previewCanaryState] = usePreviewCanaryHistoryMappingMutation()
  const suggestState = props.provider === 'pi' ? suggestPIState : suggestCanaryState
  const previewState = props.provider === 'pi' ? previewPIState : previewCanaryState
  const historySourcesKey = props.historySources.join(',')
  const onGrammarsChangeRef = React.useRef(props.onGrammarsChange)
  const onMappingValidChangeRef = React.useRef(props.onMappingValidChange)
  React.useEffect(() => {
    onGrammarsChangeRef.current = props.onGrammarsChange
    onMappingValidChangeRef.current = props.onMappingValidChange
  }, [props.onGrammarsChange, props.onMappingValidChange])
  const suggestJobQuery = useGetTagMappingJobDetailQuery(props.suggestJobId ?? '', {
    skip: !props.suggestJobId
  })
  const suggestJob = suggestJobQuery.data
  const suggestRunning =
    suggestState.isLoading ||
    suggestJob?.state === 'processing' ||
    suggestJob?.state === 'queued' ||
    suggestJob?.state === 'uploading'

  const {
    authMode: credentialAuthMode,
    username: credentialUsername,
    password: credentialPassword,
    apiToken: credentialApiToken
  } = props.credentials
  const requestBase = React.useCallback(
    () =>
      buildExternalHistoryMappingRequest({
        provider: props.provider,
        siteId: props.siteId,
        baseUrl: props.baseUrl,
        skipTlsVerify: props.skipTlsVerify,
        credentials: {
          ...(credentialAuthMode ? { authMode: credentialAuthMode } : {}),
          ...(credentialUsername !== undefined ? { username: credentialUsername } : {}),
          ...(credentialPassword !== undefined ? { password: credentialPassword } : {}),
          ...(credentialApiToken !== undefined ? { apiToken: credentialApiToken } : {})
        },
        historySources: historySourcesKey
          .split(',')
          .filter(
            (source): source is ExternalHistorySource => source === 'ignition' || source === 'opc'
          ),
        ...(props.nameFilter ? { nameFilter: props.nameFilter } : {}),
        ...(props.rootPath ? { rootPath: props.rootPath } : {}),
        ...(props.branchId ? { branchId: props.branchId } : {}),
        ...(props.groupId ? { groupId: props.groupId } : {})
      }),
    [
      props.provider,
      props.siteId,
      props.baseUrl,
      props.skipTlsVerify,
      credentialAuthMode,
      credentialUsername,
      credentialPassword,
      credentialApiToken,
      historySourcesKey,
      props.nameFilter,
      props.rootPath,
      props.branchId,
      props.groupId
    ]
  )

  // Each provider has its own RTK endpoint pair; the request body shape
  // follows the provider too, so the dispatch is narrowed here once.
  const dispatchSuggest = React.useCallback(
    (body: PIHistoryMappingInput | CanaryHistoryMappingInput) =>
      props.provider === 'pi'
        ? suggestPI(body as PIHistoryMappingInput)
        : suggestCanary(body as CanaryHistoryMappingInput),
    [props.provider, suggestCanary, suggestPI]
  )
  const dispatchPreview = React.useCallback(
    (body: PIHistoryMappingInput | CanaryHistoryMappingInput) =>
      props.provider === 'pi'
        ? previewPI(body as PIHistoryMappingInput)
        : previewCanary(body as CanaryHistoryMappingInput),
    [props.provider, previewCanary, previewPI]
  )

  const appliedSuggestJob = React.useRef<string>()
  React.useEffect(() => {
    if (!suggestJob || suggestJob.state !== 'done' || appliedSuggestJob.current === suggestJob.id) {
      return
    }
    const raw = suggestJob.result?.grammars
    if (!Array.isArray(raw) || raw.length === 0) return
    appliedSuggestJob.current = suggestJob.id
    const nextGrammars = parseStoredGrammars(JSON.stringify(raw))
    onGrammarsChangeRef.current(serializeStoredGrammars(nextGrammars))
    const nextPreview = suggestJob.result?.preview as TagMappingPreviewResult | undefined
    if (nextPreview) {
      setPreview(nextPreview)
      onMappingValidChangeRef.current(mappingIsValid(nextPreview))
    }
  }, [suggestJob])

  React.useEffect(() => {
    if (suggestRunning || (grammars.length === 0 && manuals.length === 0)) return
    let cancelled = false
    const timeout = window.setTimeout(() => {
      onMappingValidChangeRef.current(false)
      void dispatchPreview({
        ...requestBase(),
        ...(grammars.length > 0 ? { grammars } : {}),
        ...(manuals.length > 0 ? { manualLinks: manuals } : {})
      })
        .unwrap()
        .then((result) => {
          if (cancelled) return
          setPreview(result)
          onMappingValidChangeRef.current(mappingIsValid(result))
        })
        .catch(() => {
          if (cancelled) return
          setPreview(undefined)
          onMappingValidChangeRef.current(false)
        })
    }, PREVIEW_DEBOUNCE_MS)
    return () => {
      cancelled = true
      window.clearTimeout(timeout)
    }
  }, [grammars, manuals, dispatchPreview, requestBase, suggestRunning])

  const canSuggest =
    Boolean(props.siteId && props.baseUrl) &&
    externalHistoryCredentialsReady(props.provider, props.credentials) &&
    props.historySources.length > 0 &&
    !suggestRunning

  const startSuggest = () => {
    if (!canSuggest) return
    props.onMappingValidChange(false)
    setPreview(undefined)
    void dispatchSuggest({
      ...requestBase(),
      ...(props.additionalContext?.trim()
        ? { additionalContext: props.additionalContext.trim() }
        : {})
    })
      .unwrap()
      .then((result) => props.onSuggestJobIdChange(result.jobId))
  }

  const unlink = (link: { aName: string; bName: string; grammarId?: string }) => {
    props.onMappingValidChange(false)
    if (isManualLink(manuals, link)) {
      props.onManualLinksChange(serializeStoredManualLinks(removeManualLink(manuals, link)))
      return
    }
    props.onGrammarsChange(serializeStoredGrammars(excludeStoredLink(grammars, link)))
  }

  const confirmManualLinks = (counterparts: string[]) => {
    if (!linkSource) return
    const links: TagMappingLinkExclude[] = counterparts.map((name) =>
      linkSource.side === 'a'
        ? { aName: linkSource.name, bName: name }
        : { aName: name, bName: linkSource.name }
    )
    props.onManualLinksChange(serializeStoredManualLinks(addManualLinks(manuals, links)))
    props.onMappingValidChange(false)
    setLinkSource(null)
  }

  const sideLabels = { a: 'Existing tags', b: labels.records }

  return (
    <div className="flex flex-col gap-3 rounded-md border border-line-2 bg-bg-sunk p-3">
      <div className="flex flex-col gap-2">
        <Label className="text-11 font-medium text-ink-2">Existing tag sources</Label>
        <div className="flex flex-wrap gap-4">
          {(['ignition', 'opc'] as const).map((source) => (
            <button
              key={source}
              type="button"
              className="flex items-center gap-2 border-0 bg-transparent p-0 text-left"
              onClick={() => {
                const selected = props.historySources.includes(source)
                props.onHistorySourcesChange(
                  selected
                    ? props.historySources.filter((item) => item !== source)
                    : [...props.historySources, source]
                )
                props.onMappingValidChange(false)
              }}
            >
              <Checkbox checked={props.historySources.includes(source)} tabIndex={-1} />
              <span className="text-xs font-medium text-ink">
                {source === 'ignition' ? 'Ignition' : 'OPC'}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className="text-11 font-medium text-ink-2">Mapping context (optional)</Label>
        <Textarea
          value={props.additionalContext ?? ''}
          rows={3}
          maxLength={4000}
          placeholder="Naming conventions, prefixes, aliases, or unit codes…"
          onChange={(event) => props.onAdditionalContextChange(event.target.value)}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-[55ch] text-xs leading-snug text-ink-2">
          Discover every {labels.record}, infer naming grammars, then unlink or manually add
          mappings before starting ingest.
        </p>
        <Button type="button" size="sm" disabled={!canSuggest} onClick={startSuggest}>
          {suggestRunning ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Sparkles className="size-3.5" />
          )}
          {grammars.length > 0 ? 'Discover again' : 'Discover and suggest'}
        </Button>
      </div>
      {suggestState.isError ? (
        <p role="alert" className="text-xs text-st-down">
          {errorMessage(suggestState.error, labels.records)}
        </p>
      ) : null}
      {previewState.isError ? (
        <p role="alert" className="text-xs text-st-down">
          {errorMessage(previewState.error, labels.records)}
        </p>
      ) : null}
      {suggestJob?.state === 'error' ? (
        <p role="alert" className="text-xs text-st-down">
          {suggestJob.error ?? `${labels.historian} mapping suggestion failed.`}
        </p>
      ) : null}
      {props.suggestJobId && (suggestRunning || (suggestJob?.logCount ?? 0) > 0) ? (
        <details
          className="overflow-hidden rounded-md border border-line-2 bg-bg-elev"
          open={suggestRunning ? true : undefined}
        >
          <summary className="cursor-pointer px-2.5 py-1.5 text-xs font-medium text-ink">
            Mapping agent
          </summary>
          <div className="max-h-[28rem] overflow-auto border-t border-line-2 p-2">
            <JobLogs jobId={props.suggestJobId} defaultExpanded />
          </div>
        </details>
      ) : null}
      {preview ? (
        <TagMappingPreviewTables
          preview={preview}
          grammars={grammars}
          manuals={manuals}
          labels={sideLabels}
          onUnlink={unlink}
          onLink={setLinkSource}
          onRemoveGrammar={(grammarId) =>
            props.onGrammarsChange(
              serializeStoredGrammars(grammars.filter((grammar) => grammar.id !== grammarId))
            )
          }
        />
      ) : previewState.isLoading ? (
        <div role="status" className="flex items-center gap-2 text-xs text-ink-3">
          <Loader2 className="size-3.5 animate-spin" />
          Recomputing approved {labels.historian} history bindings…
        </div>
      ) : null}
      <TagMappingLinkDialog
        source={linkSource}
        preview={preview ?? {}}
        sideLabels={sideLabels}
        alreadyLinked={new Set()}
        onClose={() => setLinkSource(null)}
        onConfirm={confirmManualLinks}
      />
    </div>
  )
}
